"use client";
import React, { useState, useMemo, useEffect } from 'react'
import useCart from '@/hooks/useCart';
import { useRouter } from 'next/navigation';
import { X, ShoppingCart, Trash2, Plus, Minus, Tag, Shield, User, ArrowRight } from 'lucide-react';
import Image from 'next/image';
import Cart from "@/empty-cart.png";
import { useSelector, useDispatch } from 'react-redux';
import { clearCartWithAPI } from '@/redux/cartSlice';
import useNotifier from '@/hooks/useNotifier';
import { useStockValidation } from '@/hooks/useStockValidation';
import StockValidationModal from '@/components/StockValidationModal';
import { calculateCheckoutValues } from '@/utils/checkoutCalculations';
import { useQuery } from '@apollo/client';
import { GET_PRODUCTS_STOCK_BY_ITEM_CODES, GET_COUPONS, GET_USD } from '@/graphql/queries';
import useAuthToken from '@/hooks/useAuthToken';

// Inventory helper
import { getCartQuantityForVariantSizeAllPlatings } from '@/utility/inventoryHelpers';

// Discount helpers
import {
  buildItemCodeGroups,
  getTotalItemDiscount,
  getItemDiscountComponents,
  getItemCodeTotalQty,
  getDiscountLabel,
  calculateSizePriceWithDiscount,
  applyDiscount,
  buildProductVariantsStockMap
} from '@/utility/discountHelper';

const QuickCart = () => {
    const router = useRouter();
    const dispatch = useDispatch();
    const [isOpen, setIsOpen] = useState(false);
    const [isClearing, setIsClearing] = useState(false);
    const [showStockModal, setShowStockModal] = useState(false);

    // ── Auth ─────────────────────────────────────────────────────────────────
    // useAuthToken only exposes getToken() as a function — not a reactive value.
    // We store the result in local state and refresh it whenever the panel opens
    // so a user who logs in while the page is mounted sees the correct state.
    const { getToken } = useAuthToken();
    const [token, setToken] = useState(() => getToken());

    useEffect(() => {
        if (isOpen) {
            setToken(getToken());
        }
    }, [isOpen]);

    // ── Cart & notifications ─────────────────────────────────────────────────
    const { cartItems, removeProduct, increaseQty, decreaseQty } = useCart();
    const { successNotification, errorNotification } = useNotifier();

    const selectedCurrency = useSelector((state) => state.currency.selectedCurrency);
    const cartProducts = useSelector((state) => state.cart.products);

    const {
        validateStock,
        isValidating,
        stockIssues,
        clearIssues,
    } = useStockValidation({ cartItems });

    // ── Item codes & stock data ──────────────────────────────────────────────
    const itemCodes = useMemo(
        () => [...new Set(cartItems.map(i => i.item_code).filter(Boolean))],
        [cartItems]
    );

    const { data: stockData } = useQuery(GET_PRODUCTS_STOCK_BY_ITEM_CODES, {
        variables: { itemCodes },
        skip: itemCodes.length === 0,
        fetchPolicy: 'cache-and-network',
    });

    const productVariantsStockMap = useMemo(
        () => buildProductVariantsStockMap(stockData?.product_products ?? []),
        [stockData]
    );

    const itemCodeGroups = useMemo(
        () => buildItemCodeGroups(cartItems, productVariantsStockMap),
        [cartItems, productVariantsStockMap]
    );

    // ── Checkout calculations (subtotal, coupon, currency) ───────────────────
    // ✅ calculateCheckoutValues is a plain util (can't useQuery internally),
    // so this component fetches coupons/USD rate itself and calls it directly —
    // the same function checkout.js, the cart page, and create-payment-link
    // all use, so the quick-cart preview total can never drift from the real
    // checkout total.
    const { data: couponsData } = useQuery(GET_COUPONS);
    const { data: usdData } = useQuery(GET_USD);
    const usdConversionRate = usdData?.master_currency?.[0]?.conversion_rate || 91;

    const {
        subtotal,
        subtotalInUSD,
        couponDiscount,
        bestApplicableCoupon,
        currencySymbol,
        isINR,
        calculateItemPrice,
        allCoupons,         // needed for next-coupon progress widget
    } = useMemo(() => {
        return calculateCheckoutValues({
            cartItems,
            selectedCurrency,
            appliedCoupon: null,
            selectedRate: null,
            usdConversionRate,
            couponsData,
            itemCodeGroups,
            manualCouponId: null,
        });
    }, [cartItems, selectedCurrency, usdConversionRate, couponsData, itemCodeGroups]);

    const showUSSize = !isINR;
    const subtotalAfterDiscount = subtotal - couponDiscount;

    // ── Helpers ──────────────────────────────────────────────────────────────
    const formatPrice = (amount) => {
        const formatted = Number(amount).toFixed(2);
        if (isINR) return `${currencySymbol}${formatted}`;
        const currencyName = selectedCurrency?.name?.toUpperCase() || '';
        return `${currencySymbol} ${currencyName} ${formatted}`;
    };

    // Conversion rate: how many local-currency units per 1 USD
    const conversionRate = subtotalInUSD > 0 ? subtotal / subtotalInUSD : 1;

    // Find the next coupon the user hasn't yet unlocked (sorted by threshold asc)
    const nextCouponInfo = useMemo(() => {
        const sorted = [...(allCoupons || [])]
            .filter(c => c.min_amount_to_apply > subtotalInUSD)
            .sort((a, b) => a.min_amount_to_apply - b.min_amount_to_apply);
        const nextCoupon = sorted[0] || null;
        const amountNeededUSD = nextCoupon ? nextCoupon.min_amount_to_apply - subtotalInUSD : 0;
        const amountNeededLocal = amountNeededUSD * conversionRate;
        const thresholdLocal = nextCoupon ? nextCoupon.min_amount_to_apply * conversionRate : 0;
        const progress = nextCoupon
            ? Math.min(100, (subtotalInUSD / nextCoupon.min_amount_to_apply) * 100)
            : 100;
        const discountLabel = nextCoupon
            ? (nextCoupon.is_percentage
                ? `${nextCoupon.value}%`
                : formatPrice(nextCoupon.value * conversionRate))
            : '';
        return { nextCoupon, amountNeededLocal, thresholdLocal, progress, discountLabel };
    }, [allCoupons, subtotalInUSD, conversionRate]);

    const getMaxQuantityForItem = useMemo(() => {
        return (item, sizeSelection = null) => {
            let originalStock;
            if (sizeSelection) {
                originalStock = sizeSelection.original_stock || sizeSelection.maxQty;
            } else {
                originalStock = item.maxQty || item.original_stock;
            }
            if (!originalStock && originalStock !== 0) return null;

            let totalInCart = 0;
            if (sizeSelection?.variantSizeId) {
                totalInCart = getCartQuantityForVariantSizeAllPlatings(
                    cartProducts, item.product_id, item.product_variation_id, sizeSelection.variantSizeId
                );
            } else if (item.variant_size_id) {
                totalInCart = getCartQuantityForVariantSizeAllPlatings(
                    cartProducts, item.product_id, item.product_variation_id, item.variant_size_id
                );
            } else {
                cartProducts.forEach(cartItem => {
                    if (
                        cartItem.product_id === item.product_id &&
                        (cartItem.product_variation_id ?? null) === (item.product_variation_id ?? null) &&
                        !cartItem.variant_size_id
                    ) {
                        totalInCart += (cartItem.quantity || 0);
                    }
                });
            }
            return Math.max(0, originalStock - totalInCart);
        };
    }, [cartProducts]);

    const canIncreaseQuantity = (item, currentQty, sizeSelection = null) => {
        const remaining = getMaxQuantityForItem(item, sizeSelection);
        if (remaining === null) return true;
        return remaining > 0;
    };

    // ── Login redirect ────────────────────────────────────────────────────────
    const handleGoToLogin = () => {
        if (typeof window !== 'undefined') {
            sessionStorage.setItem('redirectAfterLogin', window.location.pathname);
        }
        setIsOpen(false);
        router.push('/login');
    };

    // ── Handlers ─────────────────────────────────────────────────────────────
    const handleClearCart = async () => {
        if (isClearing) return;
        setIsClearing(true);
        try {
            const result = await dispatch(clearCartWithAPI()).unwrap();
            if (result.success) successNotification("Cart cleared successfully!");
        } catch (error) {
            errorNotification(error || "Failed to clear cart");
        } finally {
            setIsClearing(false);
        }
    };

    const handleIncrement = async (item, itemQuantity, sizeSelection = null) => {
        if (!canIncreaseQuantity(item, itemQuantity, sizeSelection)) {
            const remaining = getMaxQuantityForItem(item, sizeSelection);
            const totalInCart = sizeSelection
                ? getCartQuantityForVariantSizeAllPlatings(cartProducts, item.product_id, item.product_variation_id, sizeSelection.variantSizeId)
                : item.variant_size_id
                    ? getCartQuantityForVariantSizeAllPlatings(cartProducts, item.product_id, item.product_variation_id, item.variant_size_id)
                    : itemQuantity;
            errorNotification(`Maximum stock reached. You have ${totalInCart} in cart across all plating options (${remaining} remaining).`);
            return;
        }
        try {
            const result = await increaseQty(
                item.product_id,
                item.product_variation_id,
                sizeSelection?.variantSizeId || item.variant_size_id,
                sizeSelection?.plating_id || item.plating_id
            );
            if (result.error) {
                errorNotification(result.payload || result.error.message || "Failed to update quantity");
            } else {
                successNotification("Quantity updated successfully");
            }
        } catch (error) {
            errorNotification(error.message || "Failed to update quantity");
        }
    };

    const handleDecrement = async (item, itemQuantity, sizeSelection = null) => {
        if (itemQuantity === 1) {
            try {
                const itemToRemove = sizeSelection ? {
                    ...item,
                    variant_size_id: sizeSelection.variantSizeId,
                    plating_id: sizeSelection.plating_id,
                    ring_size: sizeSelection.ring_size,
                    ring_inr_size: sizeSelection.ring_inr_size,
                } : item;
                await removeProduct(itemToRemove);
                successNotification("Item removed from cart");
            } catch (error) {
                errorNotification(error.message || "Failed to remove item");
            }
            return;
        }
        try {
            const result = await decreaseQty(
                item.product_id,
                item.product_variation_id,
                sizeSelection?.variantSizeId || item.variant_size_id,
                sizeSelection?.plating_id || item.plating_id
            );
            if (result.error) {
                errorNotification(result.payload || result.error.message || "Failed to update quantity");
            } else {
                successNotification("Quantity updated successfully");
            }
        } catch (error) {
            errorNotification(error.message || "Failed to update quantity");
        }
    };

    const handleRemoveSize = async (item, sizeSelection) => {
        try {
            const itemToRemove = {
                ...item,
                variant_size_id: sizeSelection.variantSizeId,
                plating_id: sizeSelection.plating_id,
                ring_size: sizeSelection.ring_size,
                ring_inr_size: sizeSelection.ring_inr_size,
            };
            await removeProduct(itemToRemove);
            successNotification("Size removed from cart");
        } catch (error) {
            errorNotification(error.message || "Failed to remove size");
        }
    };

    const handleProceedToCheckout = async () => {
        const issues = await validateStock();
        if (issues.length > 0) {
            setShowStockModal(true);
            return;
        }
        setIsOpen(false);
        router.push('/checkout');
    };

    const handleLinkClick = (path) => {
        setIsOpen(false);
        router.push(path);
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            {/* Desktop trigger — hidden on mobile */}
            <button
                onClick={() => setIsOpen(true)}
                className="hidden md:flex fixed right-4 top-1/2 transform -translate-y-1/2 rotate-90 origin-right bg-amber-900 text-white text-xs px-4 py-2 z-40 tracking-wider hover:bg-gray-800 items-center gap-2"
            >
                <ShoppingCart size={16} className="-rotate-90" />
                QUICK CHECKOUT
            </button>

            {/* Mobile trigger — small tab blended into right edge */}
            <button
                onClick={() => setIsOpen(true)}
                aria-label="Open cart"
                className="md:hidden fixed bottom-24 -right-0.5 z-40 w-8 h-8 rounded-l-xl bg-amber-900/60 text-white flex items-center justify-center hover:bg-amber-900/90 active:scale-95 transition-all duration-150"
            >
                <ShoppingCart size={14} />
            </button>

            {/* Overlay */}
            <div
                className={`fixed inset-0 z-50 transition-opacity duration-300 ${isOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
                onClick={() => setIsOpen(false)}
            >
                <div className="absolute inset-0 bg-black bg-opacity-50" />
            </div>

            {/* Offcanvas Sidebar */}
            <div
                className={`fixed top-0 right-0 z-[99] h-full w-full md:w-[520px] bg-[#f8f9fa] shadow-lg transform transition-transform duration-300 ${isOpen ? 'translate-x-0' : 'translate-x-full'} flex flex-col`}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b bg-white">
                    <div className="flex items-center gap-2">
                        <ShoppingCart size={22} className="text-[#64483c]" />
                        <h5 className="text-lg font-semibold text-[#1a1d29]">Your Cart</h5>
                        {token && cartItems?.length > 0 && (
                            <span className="text-sm text-[#6b7280]">
                                ({cartItems.length} {cartItems.length === 1 ? 'item' : 'items'})
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
             
                        <button onClick={() => setIsOpen(false)} className="text-gray-500 hover:text-gray-700 ml-2">
                            <X size={22} />
                        </button>
                    </div>
                </div>

                {/* ── Body ──────────────────────────────────────────────────── */}
                <div className="flex-grow overflow-y-auto">

                    {/* ── Not logged in ─────────────────────────────────────── */}
                    {!token ? (
                        <div className="flex flex-col items-center justify-center h-full p-8 space-y-4 text-center">
                            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center mb-1">
                                <User size={26} className="text-[#64483c]" />
                            </div>
                            <h3 className="text-lg font-semibold text-gray-800">Sign in to view your cart</h3>
                            <p className="text-gray-500 text-sm max-w-xs">
                                Log in to save items, access exclusive discounts, and check out faster.
                            </p>

                            <button
                                onClick={handleGoToLogin}
                                className="w-full max-w-xs px-6 py-2.5 bg-[#64483c] text-white rounded-lg hover:bg-gray-800 transition-colors text-sm font-medium flex items-center justify-center gap-2"
                            >
                                Sign In
                                <ArrowRight size={15} />
                            </button>

                            <button
                                onClick={() => { setIsOpen(false); router.push('/register'); }}
                                className="w-full max-w-xs px-6 py-2.5 border border-[#e5e7eb] text-[#64483c] rounded-lg hover:bg-[#f8f9fa] transition-colors text-sm"
                            >
                                Create Account
                            </button>

                            <div className="flex items-center gap-2 w-full max-w-xs">
                                <div className="flex-1 border-t border-[#e5e7eb]" />
                                <span className="text-xs text-gray-400">or</span>
                                <div className="flex-1 border-t border-[#e5e7eb]" />
                            </div>

                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-xs text-gray-400 hover:text-[#64483c] underline transition-colors"
                            >
                                Continue as guest
                            </button>
                        </div>

                    /* ── Logged in, cart empty ─────────────────────────────── */
                    ) : !cartItems || cartItems.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full p-8 space-y-4">
                            <Image
                                src={Cart.src}
                                alt="Empty Cart"
                                width={220}
                                height={220}
                                className="opacity-70 mb-2"
                            />
                            <h3 className="text-xl font-bold text-gray-800">Your Cart is Empty</h3>
                            <p className="text-gray-500 text-sm text-center max-w-xs">
                                Looks like you haven't added any items yet.
                            </p>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="px-6 py-2 bg-[#64483c] text-white rounded-full hover:bg-gray-800 transition-colors text-sm"
                            >
                                Continue Shopping
                            </button>
                        </div>

                    /* ── Logged in, has items ──────────────────────────────── */
                    ) : (
                        <div className="bg-white divide-y divide-gray-100">
                            {cartItems.flatMap((item) => {
                                const hasMultipleSizes = item.wholesaleSelections && item.wholesaleSelections.length > 0;
                                const { quantityDiscount, lowInventoryDiscount, totalDiscount } =
                                    getItemDiscountComponents(item, itemCodeGroups);
                                const itemCodeTotalQty = getItemCodeTotalQty(item, itemCodeGroups);

                                if (hasMultipleSizes) {
                                    return item.wholesaleSelections.map((selection) => {
                                        const itemKey = `${item.product_id}-${item.product_variation_id}-${selection.variantSizeId}-${selection.plating_id || 'no-plating'}`;
                                        const displaySize = showUSSize
                                            ? (selection.ring_size || selection.display_size)
                                            : (selection.ring_inr_size || selection.display_size);

                                        const unitPrice = calculateSizePriceWithDiscount(item, selection, selectedCurrency, totalDiscount);
                                        const sizePrice = unitPrice * selection.qty;
                                        const originalUnitPrice = totalDiscount > 0 ? unitPrice / (1 - totalDiscount) : unitPrice;

                                        const canIncrease = canIncreaseQuantity(item, selection.qty, selection);
                                        const remaining = getMaxQuantityForItem(item, selection);
                                        const totalInCart = getCartQuantityForVariantSizeAllPlatings(
                                            cartProducts, item.product_id, item.product_variation_id, selection.variantSizeId
                                        );

                                        return (
                                            <div key={itemKey} className="p-4 hover:bg-[#f8f9fa] transition-colors">
                                                <div className="flex gap-3">
                                                    <div
                                                        className="flex-shrink-0 cursor-pointer"
                                                        onClick={() => { setIsOpen(false); router.push(`/product-detail/${item.product_id}`); }}
                                                    >
                                                        <div className="h-20 w-20 rounded-lg overflow-hidden bg-[#f8f9fa]">
                                                            <img
                                                                src={item.image || '/default/product.png'}
                                                                alt={item.name}
                                                                className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                                                            />
                                                        </div>
                                                    </div>

                                                    <div className="flex-1 min-w-0">
                                                        <div className="flex justify-between gap-2 mb-1">
                                                            <h6
                                                                className="text-sm font-medium text-[#1a1d29] cursor-pointer hover:text-[#64483c] line-clamp-2"
                                                                onClick={() => { setIsOpen(false); router.push(`/product-detail/${item.product_id}`); }}
                                                            >
                                                                {item.name}
                                                            </h6>
                                                            <button
                                                                onClick={() => handleRemoveSize(item, selection)}
                                                                className="text-[#9ca3af] hover:text-[#64483c] flex-shrink-0"
                                                            >
                                                                <X size={16} />
                                                            </button>
                                                        </div>

                                                        {/* Unit price row */}
                                                        <div className="text-xs text-[#6b7280] mb-2">
                                                            {totalDiscount > 0 ? (
                                                                <div className="flex flex-wrap items-center gap-1">
                                                                    <span className="line-through text-[#9ca3af]">{formatPrice(originalUnitPrice)}</span>
                                                                    <span className="font-semibold text-[#64483c]">{formatPrice(unitPrice)} ea</span>
                                                                    <span className="text-green-600">({getDiscountLabel(quantityDiscount, lowInventoryDiscount)})</span>
                                                                </div>
                                                            ) : (
                                                                <span>{formatPrice(unitPrice)} each</span>
                                                            )}
                                                        </div>

                                                        {/* Badges */}
                                                        <div className="flex flex-wrap gap-1 mb-2">
                                                            {displaySize && (
                                                                <span className="px-2 py-0.5 bg-[#f8f9fa] text-[#64483c] text-xs rounded">
                                                                    Size {displaySize}
                                                                </span>
                                                            )}
                                                            {selection.plating_name && (
                                                                <span className="px-2 py-0.5 bg-[#f8f9fa] text-[#64483c] text-xs rounded">
                                                                    {selection.plating_name}
                                                                </span>
                                                            )}
                                                            {quantityDiscount > 0 && (
                                                                <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                                                                    {itemCodeTotalQty} pcs • {(quantityDiscount * 100).toFixed(0)}% Bulk
                                                                </span>
                                                            )}
                                                            {lowInventoryDiscount > 0 && (
                                                                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded">
                                                                    10% Low Stock
                                                                </span>
                                                            )}
                                                            {remaining !== null && remaining <= 5 && (
                                                                <span className="px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded">
                                                                    {remaining === 0 ? 'Max Qty' : `Only ${remaining} left`}
                                                                </span>
                                                            )}
                                                        </div>

                                                        {/* Qty stepper + line total */}
                                                        <div className="flex items-center justify-between gap-2">
                                                            <div className="inline-flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden">
                                                                <button
                                                                    onClick={() => handleDecrement(item, selection.qty, selection)}
                                                                    className="w-8 h-8 flex items-center justify-center hover:bg-[#f8f9fa] text-[#6b7280]"
                                                                >
                                                                    <Minus size={13} />
                                                                </button>
                                                                <span className="w-10 text-center text-[#1a1d29] text-sm font-medium">
                                                                    {selection.qty}
                                                                </span>
                                                                <button
                                                                    onClick={() => canIncrease && handleIncrement(item, selection.qty, selection)}
                                                                    disabled={!canIncrease}
                                                                    title={!canIncrease ? `Max stock (${totalInCart} in cart, ${remaining} remaining)` : ''}
                                                                    className={`w-8 h-8 flex items-center justify-center transition-colors ${!canIncrease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-[#f8f9fa] text-[#6b7280]'}`}
                                                                >
                                                                    <Plus size={13} />
                                                                </button>
                                                            </div>
                                                            <p className="text-base font-semibold text-[#64483c]">
                                                                {formatPrice(sizePrice)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    });
                                } else {
                                    const itemKey = `${item.product_id}-${item.product_variation_id}-${item.variant_size_id || 'no-size'}-${item.plating_id || 'no-plating'}`;
                                    const baseItemPrice = calculateItemPrice(item, item.quantity);
                                    const discountedItemPrice = applyDiscount(baseItemPrice, totalDiscount);
                                    const baseUnitPrice = calculateItemPrice(item, 1);
                                    const discountedUnitPrice = applyDiscount(baseUnitPrice, totalDiscount);
                                    const originalUnitPrice = baseUnitPrice;

                                    const canIncrease = canIncreaseQuantity(item, item.quantity);
                                    const remaining = getMaxQuantityForItem(item);
                                    let totalInCart = item.quantity;
                                    if (item.variant_size_id) {
                                        totalInCart = getCartQuantityForVariantSizeAllPlatings(
                                            cartProducts, item.product_id, item.product_variation_id, item.variant_size_id
                                        );
                                    }

                                    return (
                                        <div key={itemKey} className="p-4 hover:bg-[#f8f9fa] transition-colors">
                                            <div className="flex gap-3">
                                                <div
                                                    className="flex-shrink-0 cursor-pointer"
                                                    onClick={() => { setIsOpen(false); router.push(`/product-detail/${item.product_id}`); }}
                                                >
                                                    <div className="h-20 w-20 rounded-lg overflow-hidden bg-[#f8f9fa]">
                                                        <img
                                                            src={item.image || '/default/product.png'}
                                                            alt={item.name}
                                                            className="h-full w-full object-cover hover:scale-105 transition-transform duration-300"
                                                        />
                                                    </div>
                                                </div>

                                                <div className="flex-1 min-w-0">
                                                    <div className="flex justify-between gap-2 mb-1">
                                                        <h6
                                                            className="text-sm font-medium text-[#1a1d29] cursor-pointer hover:text-[#64483c] line-clamp-2"
                                                            onClick={() => { setIsOpen(false); router.push(`/product-detail/${item.product_id}`); }}
                                                        >
                                                            {item.name}
                                                        </h6>
                                                        <button
                                                            onClick={() => removeProduct(item)}
                                                            className="text-[#9ca3af] hover:text-[#64483c] flex-shrink-0"
                                                        >
                                                            <X size={16} />
                                                        </button>
                                                    </div>

                                                    {/* Unit price row */}
                                                    <div className="text-xs text-[#6b7280] mb-2">
                                                        {totalDiscount > 0 ? (
                                                            <div className="flex flex-wrap items-center gap-1">
                                                                <span className="line-through text-[#9ca3af]">{formatPrice(originalUnitPrice)}</span>
                                                                <span className="font-semibold text-[#64483c]">{formatPrice(discountedUnitPrice)} ea</span>
                                                                <span className="text-green-600">({getDiscountLabel(quantityDiscount, lowInventoryDiscount)})</span>
                                                            </div>
                                                        ) : (
                                                            <span>{formatPrice(discountedUnitPrice)} each</span>
                                                        )}
                                                    </div>

                                                    {/* Badges */}
                                                    {(item.ring_size || item.ring_inr_size || item.plating_name || totalDiscount > 0 || (remaining !== null && remaining <= 5)) && (
                                                        <div className="flex flex-wrap gap-1 mb-2">
                                                            {(item.ring_size || item.ring_inr_size) && (
                                                                <span className="px-2 py-0.5 bg-[#f8f9fa] text-[#64483c] text-xs rounded">
                                                                    Size {showUSSize ? item.ring_size : item.ring_inr_size}
                                                                </span>
                                                            )}
                                                            {item.plating_name && (
                                                                <span className="px-2 py-0.5 bg-[#f8f9fa] text-[#64483c] text-xs rounded">
                                                                    {item.plating_name}
                                                                </span>
                                                            )}
                                                            {quantityDiscount > 0 && (
                                                                <span className="px-2 py-0.5 bg-green-50 text-green-700 text-xs rounded">
                                                                    {itemCodeTotalQty} pcs • {(quantityDiscount * 100).toFixed(0)}% Bulk
                                                                </span>
                                                            )}
                                                            {lowInventoryDiscount > 0 && (
                                                                <span className="px-2 py-0.5 bg-amber-50 text-amber-700 text-xs rounded">
                                                                    10% Low Stock
                                                                </span>
                                                            )}
                                                            {remaining !== null && remaining <= 5 && (
                                                                <span className="px-2 py-0.5 bg-red-50 text-red-700 text-xs rounded">
                                                                    {remaining === 0 ? 'Max Qty' : `Only ${remaining} left`}
                                                                </span>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Qty stepper + line total */}
                                                    <div className="flex items-center justify-between gap-2">
                                                        <div className="inline-flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden">
                                                            <button
                                                                onClick={() => handleDecrement(item, item.quantity)}
                                                                className="w-8 h-8 flex items-center justify-center hover:bg-[#f8f9fa] text-[#6b7280]"
                                                            >
                                                                <Minus size={13} />
                                                            </button>
                                                            <span className="w-10 text-center text-[#1a1d29] text-sm font-medium">
                                                                {item.quantity}
                                                            </span>
                                                            <button
                                                                onClick={() => canIncrease && handleIncrement(item, item.quantity)}
                                                                disabled={!canIncrease}
                                                                title={!canIncrease ? `Max stock (${totalInCart} in cart, ${remaining} remaining)` : ''}
                                                                className={`w-8 h-8 flex items-center justify-center transition-colors ${!canIncrease ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'hover:bg-[#f8f9fa] text-[#6b7280]'}`}
                                                            >
                                                                <Plus size={13} />
                                                            </button>
                                                        </div>
                                                        <p className="text-base font-semibold text-[#64483c]">
                                                            {formatPrice(discountedItemPrice)}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                }
                            })}
                        </div>
                    )}
                </div>

                {/* ── Footer / Order Summary — only when logged in AND has items ── */}
                {token && cartItems?.length > 0 && (
                    <div className="border-t bg-white p-4 space-y-3">

                        {/* Subtotal row */}
                        <div className="flex justify-between text-sm">
                            <span className="text-[#6b7280]">Subtotal</span>
                            <span className="font-medium text-[#1a1d29]">{formatPrice(subtotal)}</span>
                        </div>

                        {/* ── Auto-applied coupon banner ── */}
                        {bestApplicableCoupon && couponDiscount > 0 && (
                            <div className="flex items-center justify-between p-2.5 bg-green-50 rounded-lg border border-green-200 text-xs">
                                <div className="flex items-center gap-1.5">
                                    <Tag size={13} className="text-green-600" />
                                    <span className="font-medium text-green-800">{bestApplicableCoupon.name}</span>
                                </div>
                                <span className="font-semibold text-green-700">-{formatPrice(couponDiscount)}</span>
                            </div>
                        )}

                        {/* ── Next-coupon progress widget (currency-aware) ── */}
                        {!bestApplicableCoupon && (() => {
                            const { nextCoupon, amountNeededLocal, thresholdLocal, progress, discountLabel } = nextCouponInfo;
                            return (
                                <div className="p-3 bg-amber-50 rounded-lg border border-amber-200 text-xs space-y-2">
                                    <div className="flex items-start gap-1.5">
                                        <Tag size={13} className="text-amber-700 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            {nextCoupon && amountNeededLocal > 0 ? (
                                                <>
                                                    <span className="font-medium text-amber-800">
                                                        Add{' '}
                                                        <span className="font-bold text-amber-900">
                                                            {formatPrice(amountNeededLocal)}
                                                        </span>{' '}
                                                        more to unlock{' '}
                                                    </span>
                                                    <span className="font-bold text-amber-900">
                                                        {discountLabel} off on {formatPrice(thresholdLocal)}
                                                    </span>
                                                </>
                                            ) : (
                                                <span className="font-medium text-amber-800">
                                                    Add more items to unlock discounts!
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    {nextCoupon && (
                                        <>
                                            {/* Progress bar */}
                                            <div className="h-1.5 rounded-full bg-amber-200 overflow-hidden">
                                                <div
                                                    className="h-full rounded-full bg-amber-600 transition-all duration-500"
                                                    style={{ width: `${progress}%` }}
                                                />
                                            </div>
                                            <div className="flex justify-between text-amber-700">
                                                <span>Cart: {formatPrice(subtotal)}</span>
                                  
                                            </div>
                                        </>

                                    )}
                                </div>
                            );
                        })()}

                        {/* Grand total row */}
                        <div className="flex justify-between items-baseline pt-1 border-t border-gray-100">
                            <span className="font-semibold text-[#1a1d29] text-sm">
                                {bestApplicableCoupon && couponDiscount > 0 ? 'Total after discount' : 'Total'}
                            </span>
                            <span className="text-xl font-bold text-[#64483c]">
                                {formatPrice(subtotalAfterDiscount)}
                            </span>
                        </div>

                        <p className="text-xs text-[#9ca3af]">• Shipping calculated at checkout</p>

                        {/* CTA buttons */}
                        <div className="flex gap-2 pt-1">
                            <button
                                className="flex-1 py-2.5 px-4 border border-[#e5e7eb] rounded-lg text-[#64483c] text-sm text-center hover:bg-[#f8f9fa] transition-colors"
                                onClick={() => handleLinkClick("/cart")}
                            >
                                View Cart
                            </button>
                            <button
                                onClick={handleProceedToCheckout}
                                disabled={isValidating}
                                className="flex-1 py-2.5 px-4 bg-[#64483c] text-white rounded-lg text-sm text-center hover:bg-[#a8704e] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                            >
                                {isValidating ? 'Checking…' : 'Checkout'}
                            </button>
                        </div>

                        <div className="flex items-center gap-2 text-xs text-[#9ca3af] pt-1">
                            <Shield size={14} className="text-[#64483c]" />
                            <span>Secure checkout</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Stock Validation Modal */}
            {showStockModal && (
                <StockValidationModal
                    issues={stockIssues}
                    isINR={isINR}
                    onClose={() => {
                        setShowStockModal(false);
                        clearIssues();
                    }}
                    onProceed={() => {
                        setShowStockModal(false);
                        clearIssues();
                        setIsOpen(false);
                        router.push('/checkout');
                    }}
                    onRemoveItem={async (issue) => {
                        if (issue.variant_size_id) {
                            const parentItem = cartItems.find(
                                (ci) =>
                                    ci.product_id === issue.product_id &&
                                    (ci.product_variation_id ?? null) === (issue.product_variation_id ?? null)
                            );
                            if (parentItem) {
                                const sizeSelection = {
                                    variantSizeId: issue.variant_size_id,
                                    plating_id: issue.plating_id,
                                    ring_size: issue.ring_size,
                                    ring_inr_size: issue.ring_inr_size,
                                };
                                await handleRemoveSize(parentItem, sizeSelection);
                            }
                        } else {
                            const parentItem = cartItems.find(
                                (ci) =>
                                    ci.product_id === issue.product_id &&
                                    (ci.product_variation_id ?? null) === (issue.product_variation_id ?? null)
                            );
                            if (parentItem) {
                                await removeProduct(parentItem);
                                successNotification('Item removed from cart');
                            }
                        }
                        const remaining = await validateStock();
                        if (remaining.length === 0) {
                            setShowStockModal(false);
                            clearIssues();
                        }
                    }}
                />
            )}
        </>
    );
};

export default QuickCart;